import { flushSegment, sendGroupTraitsUpdate, trackEvent } from 'utils/segment';

type SegmentUser = Record<string, any> | null | undefined;

type IntegrationDefinition = {
  name: string;
  key: string;
  partner: boolean;
  /** Whether this integration has an activate/deactivate touchpoint in V1 FE. */
  v1Trackable: boolean;
};

/**
 * Canonical reference for all 14 integration types in the analytics spec.
 *
 * The group() `integrations` object uses these keys; on each event we send only
 * the key that changed and let Segment merge traits server-side. Aggregate
 * group fields (`active_integration_count`, `has_active_integration`, etc.) are
 * NOT computed on the client — we don't have a single source of truth for
 * workspace integration state in V1, so those are owned by the backend.
 */
export const INTEGRATIONS: IntegrationDefinition[] = [
  { name: 'ABC Supply', key: 'abc_supply', partner: false, v1Trackable: false },
  { name: 'CompanyCam', key: 'companycam', partner: false, v1Trackable: true },
  { name: 'Eagleview', key: 'eagleview', partner: false, v1Trackable: true },
  { name: 'Gmail', key: 'gmail', partner: false, v1Trackable: true },
  { name: 'Hover', key: 'hover', partner: false, v1Trackable: true },
  { name: 'Nearmap', key: 'nearmap', partner: false, v1Trackable: true },
  { name: 'Nylus', key: 'nylus', partner: false, v1Trackable: true },
  { name: 'Outlook', key: 'outlook', partner: false, v1Trackable: true },
  { name: 'Quickbooks', key: 'quickbooks', partner: false, v1Trackable: true },
  {
    name: 'SalesRabbit',
    key: 'salesrabbit',
    partner: false,
    v1Trackable: false,
  },
  {
    name: 'SRS Distribution',
    key: 'srs_distribution',
    partner: true,
    v1Trackable: true,
  },
  {
    name: 'Stripe Connect',
    key: 'stripe_connect',
    partner: true,
    v1Trackable: true,
  },
  {
    name: 'Stripe Standard',
    key: 'stripe_standard',
    partner: true,
    v1Trackable: true,
  },
  { name: 'Zapier', key: 'zapier', partner: false, v1Trackable: false },
];

const KEY_BY_NAME: Record<string, string> = {};
const PARTNER_BY_KEY: Record<string, boolean> = {};
INTEGRATIONS.forEach((i) => {
  KEY_BY_NAME[i.name] = i.key;
  PARTNER_BY_KEY[i.key] = i.partner;
});

/** Profile email/calendar provider keys -> analytics integration_name. */
export const EMAIL_PROVIDER_INTEGRATION_NAME: Record<string, string> = {
  google: 'Gmail',
  outlook: 'Outlook',
  hotmail: 'Outlook',
  nylas: 'Nylus',
};

const getWorkspaceId = (user: SegmentUser): string | null => {
  const company = user?.company || user?.primary_company;
  if (company?.id != null) {
    return String(company.id);
  }
  return null;
};

type TrackIntegrationChangeArgs = {
  integrationName: string;
  activated: boolean;
  user?: SegmentUser;
  /**
   * Set to `true` ONLY when the caller is about to redirect via a
   * `window.location` change, so the queued group()/track() requests aren't
   * aborted by navigation. Leave `false` everywhere else and let Segment batch.
   */
  flush?: boolean;
};

/**
 * Fires the group() + track() pair for an integration activation/deactivation.
 *
 * No extra API calls are made — all workspace data is read from the passed
 * `user` object, and the group() call sends only the single integration key
 * that changed (Segment merges it into the workspace `integrations` trait).
 */
export const trackIntegrationChange = async ({
  integrationName,
  activated,
  user,
  flush = false,
}: TrackIntegrationChangeArgs): Promise<boolean> => {
  // Analytics must never break the app. The entire body is guarded so this
  // function can never throw or reject — worst case it logs and returns false.
  try {
    const key = KEY_BY_NAME[integrationName];
    if (!key) {
      return false;
    }

    const workspaceId = getWorkspaceId(user);

    const groupTraits: Record<string, any> = {
      integrations: {
        [key]: Boolean(activated),
      },
    };

    if (activated) {
      groupTraits.last_integration_activated_at = new Date().toISOString();
      if (PARTNER_BY_KEY[key]) {
        groupTraits.has_partner_integration = true;
      }
    }

    if (workspaceId) {
      sendGroupTraitsUpdate(workspaceId, groupTraits);
    }

    // `workspace_company_name` and `user_role` are enriched onto every event by
    // `trackEvent` (via the user object), so we don't duplicate them here.
    trackEvent(
      activated ? 'Integration Activated' : 'Integration Deactivated',
      {
        integration_name: integrationName,
        partner_integration: PARTNER_BY_KEY[key],
        workspace_id: workspaceId || undefined,
        ...(workspaceId ? { $groups: { segment_group: workspaceId } } : {}),
      },
      user
    );

    if (flush) {
      await flushSegment();
    }

    return true;
  } catch (error: any) {
    console.warn(
      'Integration analytics tracking failed:',
      error?.message || error
    );
    return false;
  }
};

export const trackIntegrationActivated = (
  integrationName: string,
  user?: SegmentUser,
  { flush = false }: { flush?: boolean } = {}
): Promise<boolean> =>
  trackIntegrationChange({ integrationName, activated: true, user, flush });

export const trackIntegrationDeactivated = (
  integrationName: string,
  user?: SegmentUser,
  { flush = false }: { flush?: boolean } = {}
): Promise<boolean> =>
  trackIntegrationChange({ integrationName, activated: false, user, flush });
